/* חשיפה הדרגתית בגלילה — מקבילה ל-IntersectionObserver שבאתר המקורי.
   מכבד את העדפת המשתמש לצמצום תנועה: במצב כזה התוכן מוצג מיד. */
import { useEffect, useRef, useState, type RefObject } from 'react';

const REVEAL_THRESHOLD = 0.12;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface ScrollRevealResult<TElement extends HTMLElement> {
  /* React 18 מצפה ל-RefObject<T> ב-prop של ref, ולכן הטיפוס אינו כולל null */
  ref: RefObject<TElement>;
  isRevealed: boolean;
}

/** מחזיר ref לאלמנט ודגל המסמן אם הוא נחשף בגלילה. */
export function useScrollReveal<TElement extends HTMLElement>(): ScrollRevealResult<TElement> {
  const ref = useRef<TElement>(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    if (prefersReducedMotion()) {
      setIsRevealed(true);

      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsRevealed(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: REVEAL_THRESHOLD },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, isRevealed };
}
